import { Text } from '@mantine/core';
import { ReactNode } from 'react';
import { UnderlineTooltip } from 'src/UI/common';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { frequencyExplanation, MISSING_AMOUNT_EXPLANATION } from '../leftOut';
import { NOT_RECORDED } from '../notRecorded';
import { calculateAnnualAmount, CashflowFrequency, Period } from '../utils';
import { formatMoney, TenantCurrency } from 'src/common/currency';

function missingAmountCell(): ReactNode {
  return (
    <UnderlineTooltip
      label={MISSING_AMOUNT_EXPLANATION}
      size="xs"
      fw={FONT_WEIGHTS.medium}
      c="gray.6"
    >
      {NOT_RECORDED}
    </UnderlineTooltip>
  );
}

/**
 * `partialNote` marks a shared item's figure that only some of its members have an amount for,
 * so the adviser reads it as incomplete rather than as the whole item.
 */
export function amountCell(
  amount: number | null | undefined,
  currency: TenantCurrency,
  partialNote?: string,
): ReactNode {
  if (amount == null) return missingAmountCell();
  if (!partialNote) return formatMoney(amount, currency);
  return (
    <UnderlineTooltip label={partialNote} size="xs" fw={FONT_WEIGHTS.medium} c="gray.6">
      {formatMoney(amount, currency)}
    </UnderlineTooltip>
  );
}

export function frequencyAmountCell(
  period: Period,
  recorded: number | null | undefined,
  frequency: CashflowFrequency | null | undefined,
  currency: TenantCurrency,
  options?: { positive?: boolean; partialNote?: string },
): ReactNode {
  if (recorded == null) return missingAmountCell();

  const prefix = options?.positive ? '+' : '';
  const annually = calculateAnnualAmount(recorded, frequency);

  if (annually === null) {
    return (
      <UnderlineTooltip
        label={frequencyExplanation(frequency)}
        size="xs"
        fw={FONT_WEIGHTS.medium}
        c="gray.6"
      >
        {prefix}
        {formatMoney(recorded, currency)}
      </UnderlineTooltip>
    );
  }

  const monthly = annually / 12;
  const displayed = period === 'monthly' ? monthly : annually;

  if (options?.partialNote) {
    return (
      <UnderlineTooltip label={options.partialNote} size="xs" fw={FONT_WEIGHTS.medium} c="gray.6">
        {prefix}
        {formatMoney(displayed, currency)}
      </UnderlineTooltip>
    );
  }

  const label =
    period === 'monthly'
      ? `Annually: ${formatMoney(annually, currency)}`
      : `Monthly: ${formatMoney(monthly, currency)}`;

  return (
    <UnderlineTooltip label={label} size="xs" fw={FONT_WEIGHTS.medium} c="black">
      {prefix}
      {formatMoney(displayed, currency)}
    </UnderlineTooltip>
  );
}

export function SectionTotal({
  children,
  tooltipLabel,
}: {
  children: ReactNode;
  tooltipLabel?: string;
}) {
  if (!tooltipLabel) {
    return (
      <Text size="lg" fw={FONT_WEIGHTS.bold} c="gray.10" style={{ whiteSpace: 'nowrap' }}>
        {children}
      </Text>
    );
  }

  return (
    <UnderlineTooltip label={tooltipLabel} size="lg" fw={FONT_WEIGHTS.bold}>
      {children}
    </UnderlineTooltip>
  );
}
